import { forwardRef, useEffect, useImperativeHandle, useMemo, useState } from "react";
import { useFormik } from "formik";
import { object, string } from "yup";
import cn from "classnames";

import { maskAccountNumber } from "../wireUtils";
import {
  NAME_RE,
  STREET_RE,
  hasLetter,
  normalizeCode,
  resolveCountryCode,
  validateAccountNumber,
  validateBirthDate,
  validateRoutingNumber,
} from "./bankAccountValidation";

export const BENEFICIARY_TYPES = ["Individual", "Business"];
export const BANK_ACCOUNT_TYPES = ["Checking", "Savings"];

// Codes are stored normalised: upper-case, without spaces or hyphens
const CODE_FIELDS = ["routingNumber", "accountNumber"];

const text = (label, { min = 2, max = 100, pattern, patternMessage } = {}) => {
  let rule = string()
    .trim()
    .required("Required field")
    .min(min, `${label} must be at least ${min} characters`)
    .max(max, `${label} must be at most ${max} characters`)
    .test("has-letter", `${label} must contain letters`, (v) => !v || hasLetter(v));
  if (pattern) rule = rule.matches(pattern, patternMessage);
  return rule;
};

const isIndividual = (values) => values.beneficiaryType !== "Business";

// Countries list is injected so the schema can map the country name to its ISO code
const buildSchema = (countries) =>
  object({
    beneficiaryName: text("Name", {
      pattern: NAME_RE,
      patternMessage: "Use Latin letters, spaces, apostrophes, dots or hyphens",
    }),
    beneficiaryEmail: string()
      .trim()
      .required("Required field")
      .max(254, "Email is too long")
      .email("Invalid email"),
    beneficiaryType: string().required("Required field").oneOf(BENEFICIARY_TYPES),
    country: string().required("Select bank country"),
    bankName: text("Bank name", {
      pattern: STREET_RE,
      patternMessage: "Bank name contains unsupported characters",
    }),
    routingNumber: string()
      .required("Required field")
      .test("routing", function routing(value) {
        const detail = validateRoutingNumber(value, resolveCountryCode(this.parent.country, countries));
        return detail ? this.createError({ message: `Check the bank code and try again. ${detail}.` }) : true;
      }),
    accountNumber: string()
      .required("Required field")
      .test("account", function account(value) {
        const detail = validateAccountNumber(value, resolveCountryCode(this.parent.country, countries));
        return detail ? this.createError({ message: `Check the account number / IBAN. ${detail}.` }) : true;
      }),
    accountType: string().required("Required field").oneOf(BANK_ACCOUNT_TYPES, "Select an account type"),
    birthDate: string().test("birth-date", function birthDate(value) {
      if (!isIndividual(this.parent)) return true;
      if (!value) return this.createError({ message: "Date of Birth is required for an individual." });
      const message = validateBirthDate(value);
      return message ? this.createError({ message }) : true;
    }),
    birthPlace: string()
      .trim()
      .test("birth-place", function birthPlace(value) {
        if (!isIndividual(this.parent)) return true;
        if (!value) return this.createError({ message: "Place of Birth is required for an individual." });
        if (value.length < 2 || value.length > 100 || !hasLetter(value) || !STREET_RE.test(value)) {
          return this.createError({ message: "Place of birth contains unsupported characters" });
        }
        return true;
      }),
  });

// Declared at module level on purpose: a component created inside the render
// function is a new type on every render, which remounts the inputs and loses focus.
const Row = ({ name, label, hint, required, readOnly, children, error }) => (
  <div className={cn("pm-row", { "pm-row-readonly": readOnly })}>
    <label className="pm-label" htmlFor={name ? `bank-${name}` : undefined}>
      <span className="pm-label-text">
        {label}
        {required && <span className="pm-required">*</span>}
      </span>
      {hint && <span className="pm-label-hint">{hint}</span>}
    </label>
    <div className={cn("pm-field", { "error-field": error })}>
      {children}
      {error && <div className="error-text">{error}</div>}
    </div>
  </div>
);

const ReadOnly = ({ id, value }) => (
  <div className="pm-input pm-readonly" id={id} aria-readonly="true">
    <span>{value || "—"}</span>
    <span className="pm-readonly-tag">Read only</span>
  </div>
);

const emptyValues = {
  beneficiaryName: "",
  beneficiaryEmail: "",
  beneficiaryType: "Individual",
  country: "",
  bankName: "",
  routingNumber: "",
  accountNumber: "",
  accountType: "",
  birthDate: "",
  birthPlace: "",
};

const toISODate = (value) => {
  if (!value) return "";
  const d = new Date(value);
  return Number.isNaN(d.getTime()) ? "" : d.toISOString().slice(0, 10);
};

/** "100 Example Street, New York, NY 10001, United States" from the application address. */
export const formatPaymentAddress = (address = {}) => {
  const streetLine = [address.street, address.street2].filter(Boolean).join(", ");
  const cityLine = [address.city, [address.state, address.postalCode].filter(Boolean).join(" ")]
    .filter(Boolean)
    .join(", ");
  return [streetLine, cityLine, address.country].filter(Boolean).join(", ");
};

/**
 * Bank account form without its own buttons: the hosting page triggers
 * `submit()` / `reset()` through the ref and reads dirty / valid through
 * `onStateChange`.
 *
 * `account` is the trader's application: trader ID, name, email, address and
 * date of birth are read from it (the beneficiary must be the account holder).
 */
const BankAccountForm = forwardRef(
  ({ method, countries, account, onSubmit, onStateChange }, ref) => {
    const validationSchema = useMemo(() => buildSchema(countries), [countries]);

    const personal = account?.personal || {};
    const profileName = [personal.firstName, personal.lastName].filter(Boolean).join(" ");
    const traderId = account?.reference?.client || "";
    const paymentAddress = formatPaymentAddress(account?.address);

    const initialValues = useMemo(
      () => ({
        ...emptyValues,
        ...(method || {}),
        beneficiaryName: profileName || method?.beneficiaryName || "",
        beneficiaryEmail: method?.beneficiaryEmail || personal.email || "",
        beneficiaryType: method?.beneficiaryType || "Individual",
        accountType: BANK_ACCOUNT_TYPES.includes(method?.accountType) ? method.accountType : "",
        birthDate: toISODate(method?.birthDate || account?.dateofbirth),
      }),
      // eslint-disable-next-line react-hooks/exhaustive-deps
      [method, profileName, personal.email, account?.dateofbirth]
    );

    const formik = useFormik({
      initialValues,
      enableReinitialize: true,
      validationSchema,
      validateOnChange: true,
      validateOnBlur: true,
      onSubmit: (values) => {
        const address = account?.address || {};
        const cleaned = Object.fromEntries(
          Object.keys(emptyValues).map((k) => {
            const raw = typeof values[k] === "string" ? values[k].trim() : values[k] ?? "";
            return [k, CODE_FIELDS.includes(k) ? normalizeCode(raw) : raw];
          })
        );
        onSubmit({
          ...cleaned,
          // payment address comes from the verified application address
          street: [address.street, address.street2].filter(Boolean).join(", "),
          city: address.city || "",
          state: address.state || "",
          postCode: address.postalCode || "",
        });
      },
    });

    const { submitForm, resetForm, dirty, isValid } = formik;

    useImperativeHandle(ref, () => ({ submit: submitForm, reset: () => resetForm() }), [
      submitForm,
      resetForm,
    ]);

    useEffect(() => {
      onStateChange?.({ dirty, valid: isValid });
    }, [dirty, isValid, onStateChange]);

    // Saved account number is shown masked until the trader starts editing it
    const savedAccountNumber = method?.accountNumber || "";
    const [accountEditing, setAccountEditing] = useState(false);
    const accountValue = formik.values.accountNumber || "";
    const showMaskedAccount = !accountEditing && !!savedAccountNumber && accountValue === savedAccountNumber;

    const countryOptions = (countries || [])
      .filter((c) => (typeof c === "string" ? true : c?.allowtrade !== false))
      .map((c) => (typeof c === "string" ? c : c.name))
      .filter(Boolean);

    const countryCode = resolveCountryCode(formik.values.country, countries);
    const isUS = countryCode === "US";
    const individual = isIndividual(formik.values);

    const bankCodeField = !countryCode
      ? { label: "Bank code", placeholder: "Select bank country first", hint: "Routing Number for US banks; BIC / SWIFT for non-US banks.", disabled: true }
      : isUS
      ? { label: "Routing Number", placeholder: "Enter routing number", hint: "Use a Routing Number for a US bank." }
      : { label: "BIC / SWIFT", placeholder: "Enter BIC / SWIFT", hint: "Enter the BIC / SWIFT code for this bank." };

    const accountField = isUS
      ? { label: "Account Number", hint: "Saved payment details are shown only where needed." }
      : { label: "Account Number / IBAN", hint: "Use the account format required by your bank." };

    const fieldError = (name) => formik.touched[name] && formik.errors[name];

    const inputProps = (name) => ({
      id: `bank-${name}`,
      name,
      value: formik.values[name] ?? "",
      onChange: formik.handleChange,
      onBlur: formik.handleBlur,
      className: "pm-input",
    });

    const handleCountryChange = (e) => {
      formik.handleChange(e);
      // bank code / account rules depend on the country
      ["routingNumber", "accountNumber"].forEach((name) => {
        if (formik.touched[name]) formik.validateField(name);
      });
    };

    const handleAccountFocus = () => {
      if (showMaskedAccount) {
        setAccountEditing(true);
        formik.setFieldValue("accountNumber", "", false);
      }
    };

    const handleAccountBlur = (e) => {
      if (accountEditing && !accountValue && savedAccountNumber) {
        // nothing typed: keep the saved number
        formik.setFieldValue("accountNumber", savedAccountNumber, false);
        setAccountEditing(false);
      }
      formik.handleBlur(e);
    };

    return (
      <form className="pm-form bank-account-form" onSubmit={formik.handleSubmit}>
        <Row label="Trader / Group ID" hint="Provided by your Zimtra account." readOnly>
          <ReadOnly id="bank-traderId" value={traderId} />
        </Row>

        <Row
          label="Beneficiary name"
          hint="The beneficiary / account holder must correspond to your Zimtra account."
          readOnly
        >
          <ReadOnly id="bank-beneficiaryName" value={formik.values.beneficiaryName} />
        </Row>

        <Row name="beneficiaryEmail" label="Beneficiary email" error={fieldError("beneficiaryEmail")}>
          <input {...inputProps("beneficiaryEmail")} type="email" placeholder="Enter email address" maxLength={254} />
        </Row>

        <Row name="beneficiaryType" label="Beneficiary type" error={fieldError("beneficiaryType")}>
          <select {...inputProps("beneficiaryType")} className="pm-input pm-select">
            {BENEFICIARY_TYPES.map((opt) => (
              <option key={opt} value={opt}>
                {opt}
              </option>
            ))}
          </select>
        </Row>

        <Row
          name="country"
          label="Bank country"
          hint="Bank country determines the bank-code field."
          required
          error={fieldError("country")}
        >
          <select {...inputProps("country")} onChange={handleCountryChange} className="pm-input pm-select">
            <option value="">Select bank country</option>
            {countryOptions.map((opt) => (
              <option key={opt} value={opt}>
                {opt}
              </option>
            ))}
          </select>
        </Row>

        <Row name="bankName" label="Bank name" required error={fieldError("bankName")}>
          <input {...inputProps("bankName")} type="text" placeholder="Enter bank name" maxLength={100} />
        </Row>

        <Row
          name="routingNumber"
          label={bankCodeField.label}
          hint={bankCodeField.hint}
          required
          error={fieldError("routingNumber")}
        >
          <input
            {...inputProps("routingNumber")}
            type="text"
            placeholder={bankCodeField.placeholder}
            disabled={bankCodeField.disabled}
            maxLength={11}
          />
        </Row>

        <Row
          name="accountNumber"
          label={accountField.label}
          hint={accountField.hint}
          required
          error={fieldError("accountNumber")}
        >
          <input
            id="bank-accountNumber"
            name="accountNumber"
            type="text"
            className="pm-input"
            placeholder="Enter account number"
            value={showMaskedAccount ? maskAccountNumber(accountValue) : accountValue}
            onFocus={handleAccountFocus}
            onChange={formik.handleChange}
            onBlur={handleAccountBlur}
            maxLength={42}
          />
        </Row>

        <Row
          name="accountType"
          label="Bank Account Type"
          hint="Checking / Savings. This is separate from beneficiary type."
          required
          error={fieldError("accountType")}
        >
          <select {...inputProps("accountType")} className="pm-input pm-select">
            <option value="">Select account type</option>
            {BANK_ACCOUNT_TYPES.map((opt) => (
              <option key={opt} value={opt}>
                {opt}
              </option>
            ))}
          </select>
        </Row>

        <Row label="Payment address" hint="To change your payment address, contact Support." readOnly>
          <ReadOnly id="bank-paymentAddress" value={paymentAddress} />
        </Row>

        {individual && (
          <>
            <Row
              name="birthDate"
              label="Beneficiary Date of Birth"
              hint="Required for an individual."
              required
              error={fieldError("birthDate")}
            >
              <input
                {...inputProps("birthDate")}
                type="date"
                placeholder="Select a date"
                min="1900-01-01"
                max={new Date().toISOString().slice(0, 10)}
              />
            </Row>

            <Row
              name="birthPlace"
              label="Beneficiary Place of Birth"
              hint="Required for an individual."
              required
              error={fieldError("birthPlace")}
            >
              <input {...inputProps("birthPlace")} type="text" placeholder="Enter place of birth" maxLength={100} />
            </Row>
          </>
        )}
      </form>
    );
  }
);

BankAccountForm.displayName = "BankAccountForm";

export default BankAccountForm;
